import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, Search, Filter, MessageSquare } from 'lucide-react';
import logger from '@/utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EnquiryReports = () => {
  const { getAuthHeaders } = useAdmin();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/reports/enquiries`, {
        headers: getAuthHeaders()
      });
      setReport(response.data);
    } catch (error) {
      logger.error('Error fetching report:', error);
      toast.error('Failed to fetch enquiry report');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (enquiryId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/enquiries/${enquiryId}/status?status=${status}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Status updated');
      fetchReport();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportToCSV = () => {
    if (!report?.enquiries) return;
    
    const headers = ['Name', 'Email', 'Phone', 'Destination', 'Travel Dates', 'Travelers', 'Budget', 'Status', 'Created At'];
    const rows = report.enquiries.map(e => [
      e.name,
      e.email,
      e.phone,
      e.destination || '',
      e.travel_dates || '',
      e.travelers || '',
      e.budget || '',
      e.status || 'new',
      e.created_at
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enquiries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredEnquiries = report?.enquiries?.filter(e => {
    const matchesSearch = e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         e.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (e.destination || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || (e.status || 'new') === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enquiry-reports">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Enquiry Reports</h1>
          <p className="text-slate-500">View and manage travel enquiries</p>
        </div>
        <Button onClick={exportToCSV} variant="outline" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-800">{report?.total || 0}</p>
            <p className="text-sm text-slate-500">Total Enquiries</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{report?.by_status?.new || 0}</p>
            <p className="text-sm text-slate-500">New</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">{report?.by_status?.contacted || 0}</p>
            <p className="text-sm text-slate-500">Contacted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{report?.by_status?.converted || 0}</p>
            <p className="text-sm text-slate-500">Converted</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Destinations */}
      {report?.by_destination && Object.keys(report.by_destination).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Enquiries by Destination</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(report.by_destination)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([dest, count]) => (
                  <span
                    key={dest}
                    className="px-3 py-1 bg-slate-100 rounded-full text-sm"
                  >
                    {dest}: <strong>{count}</strong>
                  </span>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or destination..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-enquiries"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Enquiries Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="text-left text-sm text-slate-600">
                  <th className="p-4 font-semibold">Name</th>
                  <th className="p-4 font-semibold">Contact</th>
                  <th className="p-4 font-semibold">Destination</th>
                  <th className="p-4 font-semibold">Details</th>
                  <th className="p-4 font-semibold">Status</th>
                  <th className="p-4 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry.id} className="border-b hover:bg-slate-50">
                    <td className="p-4">
                      <p className="font-medium text-slate-800">{enquiry.name}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-slate-600">{enquiry.email}</p>
                      <p className="text-sm text-slate-500">{enquiry.phone}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-slate-700">{enquiry.destination || 'Not specified'}</p>
                    </td>
                    <td className="p-4 text-sm text-slate-500">
                      {enquiry.travel_dates && <p>Dates: {enquiry.travel_dates}</p>}
                      {enquiry.travelers && <p>Travelers: {enquiry.travelers}</p>}
                      {enquiry.budget && <p>Budget: {enquiry.budget}</p>}
                    </td>
                    <td className="p-4">
                      <Select
                        value={enquiry.status || 'new'}
                        onValueChange={(value) => updateStatus(enquiry.id, value)}
                      >
                        <SelectTrigger className="w-32" data-testid={`status-${enquiry.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4 text-sm text-slate-500">
                      {new Date(enquiry.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredEnquiries.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No enquiries found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnquiryReports;
